import React, { useMemo, useRef, useState } from 'react';

import {
  PAYMENT_MODE_LABELS,
  PAYMENT_MODE_META,
  allowedRefundModes,
  CASH_MODE,
} from '../../../constants/admission';
import { formatPaise } from '../../../utils/money';
import { newIdempotencyKey } from '../../../services/api/admissionPaymentsApi';
import { describeBalance } from './billModel';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
const modeChipOn = 'px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border bg-teal-600 border-teal-600 text-white';
const modeChipOff = 'px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700';

/**
 * RefundDialog (C4 · S4) — refund part/all of a deposit. Admin/supervisor only
 * (the parent perm-gates the entry action on REFUND_DEPOSIT — segregation of
 * duties). The parent deposit is pre-bound; the refundable remaining is the
 * SERVER's NET value (net collected − already refunded — a waived amount was
 * never received so it can't be handed back).
 *
 * Reason is mandatory (gates save). Mode defaults to the parent's; allowed modes
 * = the parent's mode OR cash-out (any-in → cash-out OK); a CASH-collected
 * deposit blocks card/UPI-out. Save is single-flight + X-Idempotency-Key. A raced
 * 400 REFUND_EXCEEDS_REMAINING refreshes the shown remaining from the server's
 * `remaining_paise` and keeps the values — never a silent success. In-app modal.
 *
 * C5 (additive, discharge hand-off): optional `prefillAmountPaise` seeds the
 * amount field (e.g. the exact unused-advance amount, capped to this parent's
 * refundable remaining) and optional `expectedBalancePaise` rides on the
 * submitted body as `expected_balance_paise` (contract §11 settle-intent — a
 * race loses to 409 BALANCE_CHANGED). Both are no-ops when omitted.
 *
 * @param onSubmit(body) → { success, data?, error?, code?, remaining_paise? }
 * @param onPrintVoucher(paymentId, {isRefund:true}) → opens the voucher PDF
 */
export default function RefundDialog({ admission, parent, balancePaise, onClose, onSubmit, onPrintVoucher, prefillAmountPaise, expectedBalancePaise }) {
  const idemKey = useRef(newIdempotencyKey('refund'));
  const initialRemaining = Math.max(0, Number(parent.refundable_remaining_paise) || 0);
  const initialAmountPaise = prefillAmountPaise > 0 ? Math.min(prefillAmountPaise, initialRemaining) : 0;
  const [amount, setAmount] = useState(initialAmountPaise > 0 ? String(initialAmountPaise / 100) : '');
  const [method, setMethod] = useState(CASH_MODE);
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [raced, setRaced] = useState(null);
  const [done, setDone] = useState(null);
  // Server NET cap — may be refreshed downward if a concurrent refund won.
  const [remainingPaise, setRemainingPaise] = useState(initialRemaining);

  // Owner redesign — any mode in → any of Cash/UPI/Card out (single source).
  const modes = allowedRefundModes();
  const meta = PAYMENT_MODE_META[method] || {};
  const needsReference = !!meta.reference;

  const amountNum = Number(amount);
  const amountPaise = Math.round((Number.isFinite(amountNum) ? amountNum : 0) * 100);
  const amountValid = Number.isFinite(amountNum) && amountPaise > 0;
  const over = amountValid && amountPaise > remainingPaise;
  const reasonValid = reason.trim().length > 0;
  const referenceValid = !needsReference || reference.trim().length > 0;
  const valid = amountValid && !over && reasonValid && referenceValid && modes.includes(method);

  const pickMethod = (m) => {
    setMethod(m);
    if (!PAYMENT_MODE_META[m]?.reference) setReference('');
  };

  // Refund increases what's owed: balance_after = owed + refund.
  const afterPreview = useMemo(() => {
    if (!amountValid || over) return null;
    return describeBalance((Math.round(Number(balancePaise) || 0)) + amountPaise);
  }, [amountValid, over, amountPaise, balancePaise]);

  const fillFull = () => setAmount(String(remainingPaise / 100));

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    setRaced(null);
    const res = await onSubmit({
      amount_paise: amountPaise,
      reason: reason.trim(),
      method,
      ...(needsReference ? { reference: reference.trim() } : {}),
      idempotency_key: idemKey.current,
      ...(expectedBalancePaise !== undefined ? { expected_balance_paise: expectedBalancePaise } : {}),
    });
    setBusy(false);
    if (res && res.success) {
      setDone(res.data);
      return;
    }
    if (res && res.code === 'REFUND_EXCEEDS_REMAINING' && res.remaining_paise != null) {
      // The loser of a race — refresh the cap from the server, keep the values.
      const nextRem = Math.max(0, Number(res.remaining_paise) || 0);
      setRemainingPaise(nextRem);
      setRaced(nextRem);
      return;
    }
    if (res) {
      setError(
        res.code === 'REASON_REQUIRED' ? 'A reason is required to refund.'
          : res.code === 'REFERENCE_REQUIRED' ? 'A reference number is required for a UPI or card refund.'
            : res.code === 'INVALID_REFUND_MODE' ? 'That refund mode isn’t allowed for this deposit.'
              : res.code === 'ADMISSION_NOT_ACTIVE' ? 'Refunds are only allowed while the patient is admitted. After discharge, money movement happens at final settlement.'
                : res.error || 'Could not record the refund. Check the values and try again.',
      );
    }
  };

  // ── Success: voucher hand-off ────────────────────────────────────────────
  if (done) {
    const refund = done.refund || {};
    const afterBal = describeBalance(done.balance_paise_after);
    return (
      <div className={overlayCls} data-testid="refund-dialog">
        <div className={panelCls}>
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Refund recorded</h3>
            <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
          </div>
          <div className="p-4 space-y-3" data-testid="refund-success">
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[13px] p-3">
              Voucher <b className="tabular-nums">{refund.receipt_number || '—'}</b> — hand the printed copy with the cash.
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5 text-[13px] space-y-1">
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Refund paid</span><b className="tabular-nums text-slate-900 dark:text-white">{formatPaise(refund.amount_paise)}</b></div>
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">{afterBal.label} after refund</span><b className="tabular-nums text-slate-900 dark:text-white">{afterBal.text}</b></div>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Refund of an advance deposit — not a tax document.</p>
          </div>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
            <button type="button" className={btnSecondary} onClick={onClose} data-testid="refund-done">Done</button>
            {onPrintVoucher && refund.payment_id && (
              <button type="button" className={btnPrimary} onClick={() => onPrintVoucher(refund.payment_id, { isRefund: true })} data-testid="refund-print">Print voucher</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={overlayCls} data-testid="refund-dialog">
      <div className={panelCls}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Refund deposit</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12.5px] text-slate-600 dark:text-slate-300 px-3 py-2">
            Deposit <b className="text-slate-900 dark:text-white tabular-nums">{parent.receipt_number || '—'}</b> · {PAYMENT_MODE_LABELS[parent.method] || parent.method} · <b className="text-slate-900 dark:text-white">{admission.patient_name || admission.patient_id}</b>
            <div className="flex gap-4 flex-wrap mt-1.5">
              <span>Collected (net) <b className="text-slate-900 dark:text-white tabular-nums">{formatPaise(parent.net_amount_paise)}</b></span>
              <span>Already refunded <b className="text-slate-900 dark:text-white tabular-nums">{formatPaise(parent.refunded_total_paise)}</b></span>
              <span>Refundable <b className="text-teal-700 dark:text-teal-300 tabular-nums" data-testid="refund-remaining">{formatPaise(remainingPaise)}</b></span>
            </div>
          </div>

          {raced != null && (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[12.5px] p-2.5" data-testid="refund-raced">
              This deposit was refunded in another session — only <b className="tabular-nums">{formatPaise(raced)}</b> is refundable now. The figures above have been refreshed.
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="refund-error">{error}</div>
          )}

          <div>
            <label className={labelCls} htmlFor="rf-amount">Refund amount (₹)</label>
            <div className="flex gap-2 items-center flex-wrap">
              <input id="rf-amount" className={`${inputCls} max-w-[180px]`} type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" data-testid="refund-amount" />
              {remainingPaise > 0 && (
                <button type="button" onClick={fillFull} className="px-2.5 py-1.5 rounded-lg text-[12px] font-semibold border border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 hover:bg-teal-100 dark:hover:bg-teal-900/50" data-testid="refund-full">
                  Full refund · {formatPaise(remainingPaise)}
                </button>
              )}
            </div>
            {over && (
              <div className="text-[12px] text-red-600 dark:text-red-400 mt-1.5" data-testid="refund-over-error">Only <b className="tabular-nums">{formatPaise(remainingPaise)}</b> is refundable on this deposit. Enter {formatPaise(remainingPaise)} or less.</div>
            )}
          </div>

          <div>
            <label className={labelCls}>Refund by</label>
            <div className="flex gap-1.5 flex-wrap" role="group" aria-label="Refund mode">
              {modes.map((m) => (
                <button key={m} type="button" onClick={() => pickMethod(m)} className={method === m ? modeChipOn : modeChipOff} data-testid={`refund-mode-${m}`}>
                  {PAYMENT_MODE_LABELS[m] || m}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1" data-testid="refund-mode-hint">
              Refund by any mode — it needn’t match how the deposit was collected. UPI and card refunds need a reference number.
            </p>
            {needsReference && (
              <div className="mt-2">
                <label className={labelCls} htmlFor="rf-reference">{meta.refLabel || 'Reference number'} <span className="text-red-500">*</span></label>
                <input id="rf-reference" className={inputCls} type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder={meta.refLabel || 'Reference number'} data-testid="refund-reference" />
              </div>
            )}
          </div>

          <div>
            <label className={labelCls} htmlFor="rf-reason">Reason <span className="text-red-500">*</span></label>
            <textarea id="rf-reason" className={inputCls} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Excess advance — patient shifting to insurance" data-testid="refund-reason" />
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Required — printed on the voucher and stamped on the record with your name.</p>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5">
            <span className="text-[13px] text-slate-600 dark:text-slate-300">{afterPreview ? afterPreview.label : 'Balance'} after this refund</span>
            <b className="text-[15px] tabular-nums text-slate-900 dark:text-white" data-testid="refund-after">{afterPreview ? afterPreview.text : '—'}</b>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">A refund can re-trigger the low-deposit alert — the desk will see it immediately.</p>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} onClick={submit} disabled={!valid || busy} data-testid="refund-confirm">
            {busy ? 'Refunding…' : 'Refund & print voucher'}
          </button>
        </div>
      </div>
    </div>
  );
}
